import axios from 'axios'

const API_PATH = 'api/v2/admins/customers/shipments'

const CustomerShipmentsApiV2 = {

  /**
   * Get list of Customer Shipments
   * Invoke `GET api/v2/admins/customers/shipments
   * @param {Number} page - current page
   * @param {Number} per_page - data per page
   * @param {String} status - Customer::Shipment.status
   * @param {String} pickup_status - shipment pickup status
   * @param {String} order_number - Customer::Order.number
   * @param {String} order_status - Customer::Order.status
   * @param {String} recipient_name - Customer::Shipment.recipient_name
   * @param {String} recipient_phone - Customer::Shipment.recipient_phone
   * @param {String} pickup_start_date - Customer::Shipment.pickup_start_date
   * @param {String} courier - Customer::Shipment.courier
   * @param {String} courier_service_type - Customer::Shipment.courier_service_type
   * @param {String} destination_kota - Customer::Shipment.destination_kota
   * @param {String} tracking_no - Customer::Shipment.tracking_no
   */
  get: async ({
    //set default parameters
    page                 = 1,
    per_page             = 10,
    status               = '',
    pickup_status        = '',
    order_number         = '',
    order_status         = '',
    recipient_name       = '',
    recipient_phone      = '',
    pickup_start_date    = '',
    courier              = '',
    courier_service_type = '',
    destination_kota     = '',
    tracking_no          = '',
    process              = ''
  }) => {
    const params = {
      page                 : page,
      per_page             : per_page,
      status               : status,
      pickup_status        : pickup_status,
      order_number         : order_number,
      order_status         : order_status,
      recipient_name       : recipient_name,
      recipient_phone      : recipient_phone,
      pickup_start_date    : pickup_start_date,
      courier              : courier,
      courier_service_type : courier_service_type,
      destination_kota     : destination_kota,
      tracking_no          : tracking_no,
      process              : process
    }
    const path   = API_PATH

    const response = await axios.get(path, { params })
    return response
  },
  /**
   * Create customer order shipment endpoint.
   * Invoke `POST api/v2/admins/customers/shipments`
   * @param {number} order_id - Order ID
   * @param {number} shipment_method_id - Shipment method ID
   * @param {array} order_item_ids - list of order item that will be put in one batch
   * @param {number} cost - total shipping cost
   */
  create: async (
    order,
    orderItems,
    shipmentMethod
  ) => {
    const path = API_PATH

    const body     = {
      order_id        : order.id,
      order_item_ids  : orderItems.map((item) => item.id),
      shipment_method : shipmentMethod
    }
    const response = axios.post(path, body)
    return response
  },

  /**
   * Show detail selected shipment
   * Invoke `PATCH api/v2/admins/customers/shipments/:id
   * @param {number} shipmentId - shipment_id
   */
  show: async (shipmentId) => {
    const path = `${API_PATH}/${shipmentId}`

    const response = axios.get(path, {})
    return response
  },

  /*
   * Pack selected item, needed before shipment
   * Invoke `PATCH api/v2/admins/customers/shipments/:id/pack
   * @param {number} shipmentId - shipment_id
   */
  pack: async (shipmentId, sku) => {
    const path     = `${API_PATH}/${shipmentId}/pack`
    const payload  = {
      'sku': sku
    }
    const response = axios.patch(path, payload)
    return response
  },

  /**
   * Ship selected shipment
   * Invoke `PATCH api/v2/admins/customers/shipments/:id/ship
   * @param {number} shipmentId - shipment_id
   */
  ship: async (shipmentId) => {
    const path = `${API_PATH}/${shipmentId}/ship`

    const response = axios.patch(path)
    return response
  },

  cancel: async (shipmentId) => {
    const path = `${API_PATH}/${shipmentId}/cancel`

    const response = axios.patch(path)
    return response
  },

  generate_awb: async (shipmentId) => {
    const path = `${API_PATH}/${shipmentId}/generate_awb`

    const response = axios.patch(path)
    return response
  },

  /**
   * Update selected shipment to delivered
   * Invoke `PATCH api/v2/admins/customers/shipments/:id/delivered
   * @param {number} shipmentId - shipment_id
   */
  delivered: async (shipmentId) => {
    const path = `${API_PATH}/${shipmentId}/delivered`

    const response = axios.patch(path)
    return response
  },

  /**
   * Update selected shipment to failed
   * Invoke `PATCH api/v2/admins/customers/shipments/:id/failed
   * @param {number} shipmentId - shipment_id
   */
  failed: async (shipmentId) => {
    const path = `${API_PATH}/${shipmentId}/failed`

    const response = axios.patch(path)
    return response
  },

  /**
   * Update tracking number
   * Invoke `PATCH api/v2/admins/customers/shipments/:id/tracking_number
   */
  tracking_number: async (shipmentId, payload) => {
    const path = `${API_PATH}/${shipmentId}/tracking_number`

    const response = axios.patch(path, payload)
    return response
  },

  /**
   * Update shipping address
   * Invoke `PATCH api/v2/admins/customers/shipments/:id/update_address
   */
  update_address: async (shipmentId, payload) => {
    const path = `${API_PATH}/${shipmentId}/update_address`

    const response = axios.patch(path, payload)
    return response
  },

  /**
   * Gets the shipment methods and cost available for the proposed shipment
   * - proposed shipment based on the selectedOrderItems going into the shipment
   *
   * @param {object} orderId - Customer::Order.id
   * @param {object} selectedOrderItems - selected Customer::Order::Item
   * @param {object} courier - courier filter to get shipment method with selected courier
   */
  shipmentMethods: async ({ orderId, selectedOrderItems, courier }) => {
    const path       = `${API_PATH}/shipment_methods`
    const orderItems = selectedOrderItems.map((item) => (item.id))
    const params     = {
      order_id       : orderId,
      order_item_ids : orderItems,
      courier        : courier
    }
    const response   = axios.post(path, params)
    return response
  },
  /**
   * Request pickup for a shipment
   * Invoke `POST api/v2/admins/customers/shipments/request_pickup
   * @param {array} customerShipmentIds - Array of selected shipment id
   * @param {string} expectedTime - string of selected time.
   */
  requestPickup: async (customerShipmentIds, expectedTime) => {
    const path     = `${API_PATH}/request_pickup`
    const params   = {
      customer_shipment_ids : customerShipmentIds,
      pickup_expect_time    : expectedTime
    }
    const response = axios.post(path, params)
    return response
  },

  /**
   * Tracking shipment for Anter aja
   * Invoke `POST api/v2/admins/customers/shipments/tracking
   * @param {array} customer_shipment_id - selected shipment id
   */
  tracking: async (customer_shipment_id) => {
    const endpoint = `${API_PATH}/tracking`
    const params   = { customer_shipment_id }

    const response = await axios.post(endpoint, params)

    return response
  }
}

export default CustomerShipmentsApiV2
